import React, { useEffect, useRef, useState } from 'react';

import mustache from 'mustache';

import { useDebounce } from '@/hooks/useDebounce';
import usePurchase from '@/hooks/usePurchaseData';
import useApi from '@/hooks/useApi';
import useEvent from '@/hooks/useEvent';

import { TModalContent } from '@/services/requests/Liferay/types';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';

import { clearHtmlTags } from '@/helpers/HtmlHelper/clearHtmlTags';
import { numberToBrl } from '@/helpers/StringHelper/numberToBrl';

import { SmSlider } from '../SmSlider';
import { ISlider } from '../SmSlider/types';

type TProps = {
  liferayContent: TModalContent;
  handleRedirectToChangeScreen: () => void;
};

export const CardCalculator = ({
  liferayContent,
  handleRedirectToChangeScreen,
}: TProps) => {
  const timer = useRef<NodeJS.Timeout>();

  const {
    milesLabel,
    changeLink,
    minLabel,
    maxLabel,
    totalLabel,
    paymentMethodText,
  } = liferayContent;

  const minValue = 1000;
  const [inputValue, setInputValue] = useState<number>(minValue);
  const debouncedValue = useDebounce(inputValue, 500);

  const {
    getPurchaseValues,
    currentVoucher,
    setMilesToPurchase,
    getListQuantityMilesToPurchase,
  } = usePurchase();

  if (!currentVoucher) {
    return null;
  }

  const { total, installments, installmentValue } =
    getPurchaseValues(debouncedValue);
  const totalAmmount = total;

  const milesLabelText = clearHtmlTags(milesLabel?.text || '');
  const { ApiInteractions } = useApi();
  const event = useEvent();

  const getValue = (newValue: number, prevValue: number): number => {
    return (
      (newValue >= prevValue &&
        getListQuantityMilesToPurchase().find(
          item => item >= newValue && item <= currentVoucher.availableMiles,
        )) ||
      getListQuantityMilesToPurchase()
        .reverse()
        .find(item => item <= newValue) ||
      prevValue
    );
  };

  const handleSliderChange: ISlider['onChange'] = async (newValue?) => {
    if (newValue?.curMax) {
      const value = getValue(newValue.curMax, debouncedValue);
      setInputValue(value);

      clearTimeout(timer.current);

      timer.current = setTimeout(() => {
        dispatchInteraction(
          ApiInteractions,
          'extra_mpv_ruler_select_miles_value',
          {
            recommendationId: event.data.recommendationId,
            details: { miles: value },
          },
        );
      }, 500);
    }
  };

  useEffect(() => {
    setMilesToPurchase(inputValue);
  }, [inputValue, setMilesToPurchase]);

  return (
    <>
      <div
        className="smls-b2c-modals__modal--calculator"
        data-testid="card-calculator"
      >
        <p
          className="smls-b2c-modals__modal--calculator__title"
          data-testid="calculator-input-value"
        >
          {inputValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}

          <span className="smls-b2c-modals__modal--calculator__title--text">
            {milesLabelText}
          </span>
        </p>
        {changeLink?.text && (
          <button
            type="button"
            aria-label={clearHtmlTags(changeLink.text)}
            className="smls-b2c-modals__modal--calculator__change"
            onClick={handleRedirectToChangeScreen}
          >
            {clearHtmlTags(changeLink.text)}
          </button>
        )}
      </div>
      <div className="smls-b2c-modals__modal--calculator_component">
        <SmSlider
          variant="single"
          min={minValue}
          max={currentVoucher.availableMiles}
          defaultValue={debouncedValue}
          step={1000}
          onChange={handleSliderChange}
        />
      </div>
      <div className="smls-b2c-modals__modal--calculator-informations">
        <div className="smls-b2c-modals__modal--calculator_information">
          <span className="smls-b2c-modals__modal--calculator_information__min-max">
            <strong>
              {minValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
            </strong>{' '}
            <br /> {clearHtmlTags(minLabel?.text || '')}
          </span>
          <span className="smls-b2c-modals__modal--calculator_information__min-max">
            <strong>
              {currentVoucher.availableMiles
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
            </strong>
            <br />
            {clearHtmlTags(maxLabel?.text || '')}
          </span>
        </div>
        <div className="smls-b2c-modals__modal--calculator_information">
          <span className="smls-b2c-modals__modal--calculator_information__total">
            {clearHtmlTags(totalLabel?.text || '')}
          </span>
          <span className="smls-b2c-modals__modal--calculator_information__total">
            R$ <strong>{numberToBrl(totalAmmount)}</strong>
          </span>
        </div>
        <div className="smls-b2c-modals__modal--calculator_information">
          {paymentMethodText?.text && (
            <span className="smls-b2c-modals__modal--calculator_information__installment">
              {clearHtmlTags(
                mustache.render(paymentMethodText.text, {
                  totalAmmount: numberToBrl(totalAmmount),
                  installments,
                  installmentValue: numberToBrl(installmentValue),
                }),
              )}
            </span>
          )}
        </div>
      </div>
    </>
  );
};
